/**
 * Login Page
 * 
 * Beautiful, modern login page with Threedium OAuth.
 * Users authenticate through their Threedium account.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatErrorMessage } from '../../utils/errorUtils';
import { persistLoginFrom } from '../../utils/auth';
import './Auth.css';

// SVG Icons
const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4L12 12M12 4L4 12" stroke="#a3a3a3" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const AIIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 2L9.5 5.5L13 4L10.5 7L14 8L10.5 9L13 12L9.5 10.5L8 14L6.5 10.5L3 12L5.5 9L2 8L5.5 7L3 4L6.5 5.5L8 2Z" fill="currentColor"/>
    </svg>
);

// Threedium Logo Icon
const ThreediumIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
);

export function LoginPage() {
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [error, setError] = useState('');

    const { loginWithThreedium, error: authError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogin = async () => {
        setError('');
        setIsRedirecting(true);
        persistLoginFrom(location.state?.from);

        try {
            const result = await loginWithThreedium();
            if (result && result.success === false) {
                setError(result.error || authError || 'Failed to connect to Threedium. Please try again.');
                setIsRedirecting(false);
            }
            // Success redirects away from this page
        } catch (err) {
            setError(err.message || authError || 'Failed to connect to Threedium. Please try again.');
            setIsRedirecting(false);
        }
    };

    const handleClose = () => {
        navigate('/');
    };

    return (
        <div className="auth-page-new">
            {/* Background */}
            <div className="auth-page-bg"></div>

            {/* Floating Modal */}
            <div className="auth-modal">
                {/* Close Button */}
                <button className="auth-modal-close" onClick={handleClose}>
                    <CloseIcon />
                </button>

                {/* Left Side - Image & Features */}
                <div className="auth-modal-left">
                    <img 
                        src="/assets/auth/login-bg.png" 
                        alt="" 
                        className="auth-modal-bg-image"
                    />
                    
                    {/* Hero Text */}
                    <div className="auth-hero-text">
                        <h1>
                            AI Powered<br />
                            <span className="text-accent">AI Experience </span>
                            and infrastructure
                        </h1>
                    </div>

                    {/* AI Badge */}
                    <div className="auth-ai-badge">
                        <AIIcon />
                        <span>High Quality 3D Model Generator</span>
                    </div>

                    {/* Feature Badges */}
                    <div className="auth-feature-badges">
                        <div className="auth-badge">No-code 3D</div>
                        <div className="auth-badge">Analytics Tool</div>
                        <div className="auth-badge">Optimization</div>
                        <div className="auth-badge">AI Texturing</div>
                        <div className="auth-badge">Experience Builder</div>
                        <div className="auth-badge auth-badge-highlight">
                            <AIIcon />
                            <span>3D/UI Agents</span>
                            <span className="badge-soon">Soon</span>
                        </div>
                    </div>
                </div>

                {/* Right Side - Login */}
                <div className="auth-modal-right">
                    <div className="auth-form-container" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        padding: '3rem 2.5rem',
                        textAlign: 'center'
                    }}>
                        {/* Header */}
                        <div className="auth-form-header" style={{ marginBottom: '2.5rem' }}>
                            <h2 style={{ fontSize: '1.75rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                                Welcome to<br />Threedium Agents
                            </h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                Sign in with your Threedium account to access the AI Experience Builder
                            </p>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="auth-error-new" style={{ marginBottom: '1.5rem', width: '100%' }}>
                                {formatErrorMessage(error)}
                            </div>
                        )}

                        {/* Threedium Login Button */}
                        <button
                            className="auth-submit-btn"
                            onClick={handleLogin}
                            disabled={isRedirecting}
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '10px',
                                width: '100%',
                                maxWidth: '320px',
                                padding: '14px 24px',
                                fontSize: '15px',
                                fontWeight: '500'
                            }}
                        >
                            <ThreediumIcon />
                            {isRedirecting ? 'Redirecting to Threedium...' : 'Login with Threedium'}
                        </button>

                        {/* Info text */}
                        <div style={{ marginTop: '2.5rem' }}>
                            <p style={{ color: 'var(--text-tertiary)', fontSize: '12px', lineHeight: '1.8' }}>
                                You'll be redirected to Threedium to authenticate.
                                <br />
                                Don't have a Threedium account?{' '}
                                <a 
                                    href="https://threedium.io" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ color: 'var(--accent-primary)' }}
                                >
                                    Sign up at threedium.io
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
